/**
 * Maintenance Screen
 * Shown when multiple API calls fail consecutively (server likely down)
 * Provides user-friendly message and support contact
 */

import { RefreshCw, Mail } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';

interface MaintenanceScreenProps {
  onRetry: () => void;
}

export function MaintenanceScreen({ onRetry }: MaintenanceScreenProps) {
  const handleContactSupport = () => {
    window.location.href = 'mailto:support@wlkthru.com?subject=App%20Issue%20Report';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Icon */}
        <div className="mx-auto w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
          <svg
            className="w-10 h-10 text-orange-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Message */}
        <div className="space-y-3">
          <h1 className="text-2xl font-bold text-gray-900">
            We're experiencing some issues
          </h1>
          <p className="text-gray-600">
            Our servers are temporarily unavailable. This is usually resolved quickly.
            Please try again in a moment.
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3 pt-4">
          <Button
            onClick={onRetry}
            className="w-full bg-primary hover:bg-primary/90"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>

          <Button
            onClick={handleContactSupport}
            variant="outline"
            className="w-full"
          >
            <Mail className="w-4 h-4 mr-2" />
            Contact Support
          </Button>
        </div>

        {/* Support Info */}
        <p className="text-sm text-gray-500 pt-4">
          If this issue persists, please contact us at{' '}
          <a
            href="mailto:support@wlkthru.com"
            className="text-primary hover:underline font-medium"
          >
            support@wlkthru.com
          </a>
        </p>
      </div>
    </div>
  );
}
